import { useCallback } from "react";

/**
 * useHyperlinks
 * Click handler for `<td class="has-link">` cells.  Supports:
 *   - External URLs   → window.open in a new tab.
 *   - Same-sheet refs ("#A1" / "A1") → scroll-to-cell + select.
 *   - Cross-sheet refs ("#Sheet2!B5") → switch sheet, queue scroll target,
 *     and select the cell once the new sheet has rendered.
 */
export default function useHyperlinks({
  sheetNames, activeSheet, setActiveSheet, sheetDataByName,
  colWidths, rowHeights, gridWrapperRef,
  setSelectedCell, setSelection, pendingLinkScrollRef,
}) {
  const parseCellRef = useCallback((ref) => {
    if (!ref) return null;
    let cleaned = ref.replace(/\$/g, "");
    if (cleaned.includes(":")) cleaned = cleaned.split(":")[0];
    const m = cleaned.match(/^([A-Za-z]+)(\d+)$/);
    if (!m) return null;
    const letters = m[1].toUpperCase();
    let col = 0;
    for (let i = 0; i < letters.length; i++) col = col * 26 + (letters.charCodeAt(i) - 64);
    return { row: parseInt(m[2]) - 1, col: col - 1 };
  }, []);

  const scrollToCell = useCallback((cellPos, targetColWidths, targetRowHeights) => {
    const el = gridWrapperRef.current;
    if (!el || !cellPos) return;
    const targetLeft = (targetColWidths || []).slice(0, cellPos.col).reduce((s, w) => s + w, 0);
    const targetTop = (targetRowHeights || []).slice(0, cellPos.row).reduce((s, h) => s + h, 0);
    el.scrollLeft = Math.max(0, targetLeft - 50);
    el.scrollTop = Math.max(0, targetTop - 50);
  }, [gridWrapperRef]);

  const handleLinkClick = useCallback((url) => {
    if (!url) return;
    const deFragmented = url.startsWith("#") ? url.slice(1) : url;

    if (!url.includes("://")) {
      if (deFragmented.includes("!")) {
        const bangIdx = deFragmented.lastIndexOf("!");
        const sheetPart = deFragmented.slice(0, bangIdx);
        const cellRefPart = deFragmented.slice(bangIdx + 1);
        const targetSheet = sheetPart.replace(/^'|'$/g, "");

        if (sheetNames.includes(targetSheet)) {
          const cellPos = parseCellRef(cellRefPart);
          if (cellPos) {
            setSelectedCell(cellPos);
            setSelection({ anchor: cellPos, focus: cellPos, dragging: false });
          }
          if (targetSheet !== activeSheet) {
            const targetModel = sheetDataByName[targetSheet];
            const tColWidths = targetModel?.colWidths || colWidths;
            const tRowHeights = targetModel?.rowHeights || rowHeights;
            if (cellPos) {
              const targetLeft = tColWidths.slice(0, cellPos.col).reduce((s, w) => s + w, 0);
              const targetTop = tRowHeights.slice(0, cellPos.row).reduce((s, h) => s + h, 0);
              pendingLinkScrollRef.current = {
                left: Math.max(0, targetLeft - 50),
                top: Math.max(0, targetTop - 50),
              };
            }
            setActiveSheet(targetSheet);
          } else if (cellPos) {
            scrollToCell(cellPos, colWidths, rowHeights);
          }
          return;
        }
      } else {
        const cellPos = parseCellRef(deFragmented);
        if (cellPos) {
          setSelectedCell(cellPos);
          setSelection({ anchor: cellPos, focus: cellPos, dragging: false });
          scrollToCell(cellPos, colWidths, rowHeights);
          return;
        }
      }
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }, [sheetNames, activeSheet, sheetDataByName, parseCellRef, colWidths, rowHeights,
      scrollToCell, setActiveSheet, setSelectedCell, setSelection, pendingLinkScrollRef]);

  return { handleLinkClick, parseCellRef, scrollToCell };
}
